import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { Plus, FileInput, Search, Upload, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { UploadBillModal } from '@/components/dashboard/upload-modals/UploadBillModal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api';

interface ApInvoice {
  id: string;
  supplierInvoiceNumber: string;
  vendorId: string | null;
  parsedVendorName: string | null;
  vendorName: string | null;
  sourceAttachmentId: string | null;
  status: string;
  matchStatus: string | null;
  totalAmount: string;
  amountPaid: string;
  balance: string;
  invoiceDate: string;
  dueDate?: string | null;
  createdAt: string;
}

export function ApInvoicesPage() {
  const { sessionId } = useAuth();
  const [, setLocation] = useLocation();
  
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [uploadOpen, setUploadOpen] = useState(false);

  // Fetch AP invoices
  const { data: invoices, isLoading } = useQuery({
    queryKey: ['ap-invoices', sessionId],
    queryFn: async () => {
      const result = await api.get<{ invoices: ApInvoice[] }>('/ap-invoices', sessionId);
      return result.invoices;
    },
    enabled: !!sessionId,
  });

  // Filter invoices
  const filteredInvoices = (invoices || []).filter((invoice) => {
    if (searchValue) {
      const q = searchValue.toLowerCase();
      const vendorLabel = invoice.vendorName ?? invoice.parsedVendorName ?? '';
      const matches =
        invoice.supplierInvoiceNumber.toLowerCase().includes(q) ||
        vendorLabel.toLowerCase().includes(q);
      if (!matches) return false;
    }
    if (statusFilter !== 'all' && invoice.status !== statusFilter) return false;
    return true;
  });

  const handleRowClick = (invoiceId: string) => {
    setLocation(`/ap-invoices/${invoiceId}`);
  };

  const formatCurrency = (amount: number | string | null | undefined) => {
    const n = typeof amount === 'number' ? amount : parseFloat(String(amount ?? '0')) || 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(n);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vendor Invoices</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage accounts payable invoices
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setUploadOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Upload Bill
          </Button>
          <Button onClick={() => setLocation('/ap-invoices/new')}>
            <Plus className="h-4 w-4 mr-2" />
            New Invoice
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search invoices or vendors..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="text-center py-12 text-gray-500">
              Loading vendor invoices...
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="text-center py-12">
              <FileInput className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No vendor invoices found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vendor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Due Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredInvoices.map((invoice) => (
                    <tr
                      key={invoice.id}
                      onClick={() => handleRowClick(invoice.id)}
                      className="hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">
                            {invoice.supplierInvoiceNumber}
                          </span>
                          {invoice.sourceAttachmentId && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-purple-50 text-purple-700"
                              title="Created from an AI-uploaded document"
                            >
                              <Sparkles className="h-2.5 w-2.5" /> AI
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {invoice.vendorName ?? (
                          invoice.parsedVendorName
                            ? <span className="italic text-gray-500">{invoice.parsedVendorName} (unmatched)</span>
                            : <span className="italic text-gray-400">No vendor</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={
                          invoice.status === 'paid' ? 'default' :
                          invoice.status === 'approved' ? 'secondary' :
                          invoice.status === 'overdue' ? 'destructive' :
                          invoice.status === 'cancelled' ? 'outline' :
                          'outline'
                        }>
                          {invoice.status}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(invoice.totalAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(invoice.invoiceDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {invoice.dueDate ? formatDate(invoice.dueDate) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <UploadBillModal open={uploadOpen} onOpenChange={setUploadOpen} />
    </div>
  );
}
